import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly errorMessage = signal('');

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  private getRateLimitMessage(retryAfterSeconds: unknown): string {
    const retrySeconds = Number(retryAfterSeconds);
    if (!Number.isFinite(retrySeconds) || retrySeconds <= 0) {
      return 'Too many sign-in attempts. Please wait a few minutes and try again.';
    }

    const retryAfterMinutes = Math.ceil(retrySeconds / 60);
    return retryAfterMinutes <= 1
      ? 'Too many sign-in attempts. Please wait about a minute and try again.'
      : `Too many sign-in attempts. Please wait about ${retryAfterMinutes} minutes and try again.`;
  }

  submit(): void {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    this.authService
      .login(this.form.getRawValue())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.router.navigate(['/home']);
        },
        error: (error) => {
          const apiMessage = error?.error?.message;

          this.errorMessage.set(
            error?.status === 429
              ? this.getRateLimitMessage(error?.error?.retryAfterSeconds)
              :
            error?.status === 403
              ? (typeof apiMessage === 'string' && apiMessage.trim().length > 0
                ? apiMessage
                : 'Sign-in is currently blocked for this account. Please use Forgot Password.')
              : 
            error?.status === 401
              ? 'Invalid email or password.'
              : 'Unable to sign in right now.',
          );
        },
      });
  }
}